import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import AdminLayout from "./AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem
} from "../../components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, UserCheck, UserX, Loader2, Trash2, Eye, EyeOff, Pencil, Search, Power, BarChart3, MoreVertical } from "lucide-react";
import { LANGUAGE_OPTIONS } from "../../i18n/translations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminUsers() {
  const { getAuthHeader } = useAuth();
  const [usersData, setUsersData] = useState({ active_count: 0, passive_count: 0, users: [] });
  const [packages, setPackages] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isPackageDialogOpen, setIsPackageDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({ full_name: "", email: "", password: "" });
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [assignLang, setAssignLang] = useState(localStorage.getItem("admin_lang") || "en");
  const [assignType, setAssignType] = useState("package"); // "package" or "bundle"
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    full_name: "",
    identity_no: "",
    package_id: "",
    bundle_id: "",
    native_language: "tr",
    learning_language: localStorage.getItem("admin_lang") || "en"
  });
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isStatsDialogOpen, setIsStatsDialogOpen] = useState(false);
  const [statsData, setStatsData] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch packages/bundles for BOTH languages so create/assign dropdowns can filter
      // by the chosen learning language (independent of the current admin panel language).
      const [usersRes, pkgEnRes, pkgMeRes, pkgEsRes, pkgDeRes, pkgItRes, pkgPtRes, bundleEnRes, bundleMeRes, bundleEsRes, bundleDeRes, bundleItRes, bundlePtRes] = await Promise.all([
        axios.get(`${API}/admin/users`, getAuthHeader()),
        axios.get(`${API}/admin/packages`, { ...getAuthHeader(), params: { language: "en" } }),
        axios.get(`${API}/admin/packages`, { ...getAuthHeader(), params: { language: "me" } }),
        axios.get(`${API}/admin/packages`, { ...getAuthHeader(), params: { language: "es" } }),
        axios.get(`${API}/admin/packages`, { ...getAuthHeader(), params: { language: "de" } }),
        axios.get(`${API}/admin/packages`, { ...getAuthHeader(), params: { language: "it" } }),
        axios.get(`${API}/admin/packages`, { ...getAuthHeader(), params: { language: "pt" } }),
        axios.get(`${API}/admin/bundles`, { ...getAuthHeader(), params: { language: "en" } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/bundles`, { ...getAuthHeader(), params: { language: "me" } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/bundles`, { ...getAuthHeader(), params: { language: "es" } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/bundles`, { ...getAuthHeader(), params: { language: "de" } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/bundles`, { ...getAuthHeader(), params: { language: "it" } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/bundles`, { ...getAuthHeader(), params: { language: "pt" } }).catch(() => ({ data: [] }))
      ]);
      setUsersData(usersRes.data);
      const pkgEn = (pkgEnRes.data || []).map(p => ({ ...p, language: p.language || "en" }));
      const pkgMe = (pkgMeRes.data || []).map(p => ({ ...p, language: p.language || "me" }));
      const pkgEs = (pkgEsRes.data || []).map(p => ({ ...p, language: p.language || "es" }));
      const pkgDe = (pkgDeRes.data || []).map(p => ({ ...p, language: p.language || "de" }));
      const pkgIt = (pkgItRes.data || []).map(p => ({ ...p, language: p.language || "it" }));
      const pkgPt = (pkgPtRes.data || []).map(p => ({ ...p, language: p.language || "pt" }));
      setPackages([...pkgEn, ...pkgMe, ...pkgEs, ...pkgDe, ...pkgIt, ...pkgPt].filter(p => p.is_active));
      const bundleEn = (bundleEnRes.data || []).map(b => ({ ...b, language: b.language || "en" }));
      const bundleMe = (bundleMeRes.data || []).map(b => ({ ...b, language: b.language || "me" }));
      const bundleEs = (bundleEsRes.data || []).map(b => ({ ...b, language: b.language || "es" }));
      const bundleDe = (bundleDeRes.data || []).map(b => ({ ...b, language: b.language || "de" }));
      const bundleIt = (bundleItRes.data || []).map(b => ({ ...b, language: b.language || "it" }));
      const bundlePt = (bundlePtRes.data || []).map(b => ({ ...b, language: b.language || "pt" }));
      setBundles([...bundleEn, ...bundleMe, ...bundleEs, ...bundleDe, ...bundleIt, ...bundlePt]);
    } catch (error) {
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.post(`${API}/admin/users`, formData, getAuthHeader());
      toast.success("Kullanıcı oluşturuldu");
      fetchData();
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    } finally {
      setSaving(false);
    }
  };

  const handleAssignPackage = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    
    setSaving(true);
    try {
      if (assignType === "bundle" && formData.bundle_id) {
        // Bundle ata
        await axios.put(
          `${API}/admin/users/${selectedUser.id}/bundle?bundle_id=${formData.bundle_id}`,
          {},
          getAuthHeader()
        );
        toast.success("Kombo paketi atandı");
      } else if (formData.package_id) {
        // Tekil paket ata
        await axios.put(
          `${API}/admin/users/${selectedUser.id}/package?package_id=${formData.package_id}`,
          {},
          getAuthHeader()
        );
        toast.success("Paket atandı");
      } else {
        toast.error("Lütfen bir paket veya kombo seçin");
        setSaving(false);
        return;
      }
      fetchData();
      setIsPackageDialogOpen(false);
      setSelectedUser(null);
      setAssignType("package");
      setFormData(prev => ({ ...prev, package_id: "", bundle_id: "" }));
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`"${userName}" kullanıcısını kalıcı olarak silmek istediğinize emin misiniz? Bu işlem geri alınamaz!`)) return;

    try {
      await axios.delete(`${API}/admin/users/${userId}`, getAuthHeader());
      toast.success("Kullanıcı silindi");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Silme işlemi başarısız");
    }
  };

  const handleOpenEditDialog = (user) => {
    setSelectedUser(user);
    setEditFormData({
      full_name: user.full_name || "",
      email: user.email || "",
      password: ""
    });
    setShowEditPassword(false);
    setIsEditDialogOpen(true);
  };

  const handleOpenStats = async (user) => {
    setSelectedUser(user);
    setIsStatsDialogOpen(true);
    setStatsLoading(true);
    setStatsData(null);
    try {
      const res = await axios.get(`${API}/admin/users/${user.id}/stats`, getAuthHeader());
      setStatsData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gelişim bilgisi alınamadı");
    } finally {
      setStatsLoading(false);
    }
  };

  const handleToggleActive = async (user) => {
    const newActive = !(user.is_active !== false);
    const actionLabel = newActive ? "aktifleştirmek" : "pasife almak (dondurmak)";
    if (!window.confirm(`"${user.full_name}" kullanıcısını ${actionLabel} istediğinize emin misiniz?`)) return;

    try {
      await axios.put(
        `${API}/admin/users/${user.id}`,
        { is_active: newActive },
        getAuthHeader()
      );
      toast.success(newActive ? "Kullanıcı aktifleştirildi" : "Kullanıcı pasife alındı");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;

    if (editFormData.password && editFormData.password.length < 6) {
      toast.error("Yeni şifre en az 6 karakter olmalı");
      return;
    }

    setSaving(true);
    try {
      const payload = {};
      if (editFormData.full_name && editFormData.full_name !== selectedUser.full_name) {
        payload.full_name = editFormData.full_name.trim();
      }
      if (editFormData.email && editFormData.email !== selectedUser.email) {
        payload.email = editFormData.email.trim();
      }
      if (editFormData.password) {
        payload.password = editFormData.password;
      }

      if (Object.keys(payload).length === 0) {
        toast.info("Değişiklik yapılmadı");
        setIsEditDialogOpen(false);
        setSaving(false);
        return;
      }

      await axios.put(`${API}/admin/users/${selectedUser.id}`, payload, getAuthHeader());
      toast.success("Kullanıcı bilgileri güncellendi");
      fetchData();
      setIsEditDialogOpen(false);
      setSelectedUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Güncelleme başarısız");
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: "",
      password: "",
      full_name: "",
      identity_no: "",
      package_id: "",
      bundle_id: "",
      native_language: "tr",
      learning_language: localStorage.getItem("admin_lang") || "en"
    });
    setAssignType("package");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("tr-TR");
  };

  // Language-aware filters for the create & assign dialogs.
  const langLabel = (l) => (l === "me" ? "🇲🇪 Karadağca" : l === "es" ? "🇪🇸 İspanyolca" : l === "de" ? "🇩🇪 Almanca" : l === "it" ? "🇮🇹 İtalyanca" : l === "pt" ? "🇵🇹 Portekizce" : "🇬🇧 English");
  const ALL_LEARNING_LANGS = ["en", "es", "me", "de", "it", "pt"];
  const createPackages = packages.filter((p) => (p.language || "en") === formData.learning_language);
  const assignPackages = packages.filter((p) => (p.language || "en") === assignLang);
  const assignBundles = bundles.filter((b) => (b.language || "en") === assignLang);

  return (
    <AdminLayout title="Kullanıcılar">
      <div className="space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="border-neutral-800 bg-emerald-900/20">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-600/30 text-emerald-500 flex items-center justify-center">
                <UserCheck className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-emerald-400">Aktif Kullanıcı</p>
                <p className="text-3xl font-bold text-emerald-500">{usersData.active_count}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-neutral-800 bg-amber-900/20">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-600/30 text-amber-500 flex items-center justify-center">
                <UserX className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-amber-400">Pasif Kullanıcı</p>
                <p className="text-3xl font-bold text-amber-500">{usersData.passive_count}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between">
          <p className="text-neutral-400">Toplam {usersData.users.length} kullanıcı</p>
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-red-600 hover:bg-red-700" data-testid="add-user-btn">
                <Plus className="w-4 h-4 mr-2" />
                Yeni Kullanıcı
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md bg-neutral-900 border-neutral-800">
              <DialogHeader>
                <DialogTitle className="text-white font-['Nunito']">Yeni Kullanıcı Oluştur</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
                <div className="space-y-2">
                  <Label className="text-neutral-300">Ad Soyad *</Label>
                  <Input
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    placeholder="Ad Soyad"
                    required
                    autoComplete="off"
                    className="bg-neutral-800 border-neutral-700 text-white"
                    data-testid="user-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-neutral-300">E-posta *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="ornek@email.com"
                    required
                    autoComplete="off"
                    className="bg-neutral-800 border-neutral-700 text-white"
                    data-testid="user-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-neutral-300">Kimlik No</Label>
                  <Input
                    value={formData.identity_no}
                    onChange={(e) => setFormData({ ...formData, identity_no: e.target.value })}
                    placeholder="T.C. Kimlik No"
                    autoComplete="off"
                    className="bg-neutral-800 border-neutral-700 text-white"
                    data-testid="user-identity-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-neutral-300">Şifre *</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="••••••••"
                      required
                      autoComplete="new-password"
                      className="bg-neutral-800 border-neutral-700 text-white pr-10"
                      data-testid="user-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-white"
                      data-testid="toggle-password-visibility"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-neutral-300">Ana dil *</Label>
                  <Select value={formData.native_language} onValueChange={(v) => setFormData({ ...formData, native_language: v })}>
                    <SelectTrigger className="bg-neutral-800 border-neutral-700 text-white" data-testid="user-language-select">
                      <SelectValue placeholder="Dil seçin" />
                    </SelectTrigger>
                    <SelectContent className="bg-neutral-800 border-neutral-700">
                      {LANGUAGE_OPTIONS.map((lang) => (
                        <SelectItem key={lang.code} value={lang.code} className="text-white">{lang.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-neutral-300">Öğreneceği dil: *</Label>
                  <Select value={formData.learning_language} onValueChange={(v) => setFormData({ ...formData, learning_language: v, package_id: "" })}>
                    <SelectTrigger className="bg-neutral-800 border-neutral-700 text-white" data-testid="user-learning-language-select">
                      <SelectValue placeholder="Öğrenilecek dil" />
                    </SelectTrigger>
                    <SelectContent className="bg-neutral-800 border-neutral-700">
                      <SelectItem value="en" className="text-white">🇬🇧 English</SelectItem>
                      <SelectItem value="es" className="text-white">🇪🇸 İspanyolca</SelectItem>
                      <SelectItem value="me" className="text-white">🇲🇪 Karadağca</SelectItem>
                      <SelectItem value="de" className="text-white">🇩🇪 Almanca</SelectItem>
                      <SelectItem value="it" className="text-white">🇮🇹 İtalyanca</SelectItem>
                      <SelectItem value="pt" className="text-white">🇵🇹 Portekizce</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-neutral-300">Paket (Opsiyonel) — {langLabel(formData.learning_language)}</Label>
                  <Select value={formData.package_id} onValueChange={(v) => setFormData({ ...formData, package_id: v })}>
                    <SelectTrigger className="bg-neutral-800 border-neutral-700 text-white" data-testid="user-package-select">
                      <SelectValue placeholder="Paket seçin" />
                    </SelectTrigger>
                    <SelectContent className="bg-neutral-800 border-neutral-700">
                      {createPackages.length === 0 ? (
                        <div className="px-2 py-1.5 text-sm text-neutral-500">Bu dilde paket yok</div>
                      ) : createPackages.map((pkg) => (
                        <SelectItem key={pkg.id} value={pkg.id} className="text-white">{pkg.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full bg-red-600 hover:bg-red-700" disabled={saving} data-testid="save-user-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Oluştur
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Assign Package/Bundle Dialog */}
        <Dialog open={isPackageDialogOpen} onOpenChange={(open) => {
          setIsPackageDialogOpen(open);
          if (!open) {
            setAssignType("package");
            setFormData(prev => ({ ...prev, package_id: "", bundle_id: "" }));
          }
        }}>
          <DialogContent className="max-w-md bg-neutral-900 border-neutral-800">
            <DialogHeader>
              <DialogTitle className="text-white font-['Nunito']">Paket / Kombo Ata</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAssignPackage} className="space-y-4">
              <p className="text-sm text-neutral-400">
                <strong className="text-white">{selectedUser?.full_name}</strong> kullanıcısına paket veya kombo atayın:
              </p>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-neutral-400">Öğrencinin aktif dili:</span>
                <Badge className="bg-neutral-800 text-white border-neutral-700" data-testid="assign-user-language">
                  {langLabel(selectedUser?.learning_language || "en")}
                </Badge>
              </div>

              {/* Atanacak dil seçimi (farklı bir dil paketi de atanabilir) */}
              <div className="space-y-2">
                <Label className="text-neutral-300">Atanacak dil</Label>
                <Select value={assignLang} onValueChange={(v) => { setAssignLang(v); setFormData(prev => ({ ...prev, package_id: "", bundle_id: "" })); }}>
                  <SelectTrigger className="bg-neutral-800 border-neutral-700 text-white" data-testid="assign-language-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-neutral-800 border-neutral-700">
                    {ALL_LEARNING_LANGS.map((l) => (
                      <SelectItem key={l} value={l} className="text-white">{langLabel(l)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-neutral-500">Öğrencinin mevcut dilleri korunur; bu dil enrollment olarak eklenir.</p>
              </div>
              
              {/* Seçim Tipi */}
              <div className="flex gap-2">
                <Button 
                  type="button"
                  variant={assignType === "package" ? "default" : "outline"}
                  className={assignType === "package" ? "bg-red-600 hover:bg-red-700 flex-1" : "border-neutral-700 text-neutral-300 flex-1"}
                  onClick={() => setAssignType("package")}
                >
                  Tekil Paket
                </Button>
                <Button 
                  type="button"
                  variant={assignType === "bundle" ? "default" : "outline"}
                  className={assignType === "bundle" ? "bg-amber-600 hover:bg-amber-700 flex-1" : "border-neutral-700 text-neutral-300 flex-1"}
                  onClick={() => setAssignType("bundle")}
                >
                  📦 Kombo
                </Button>
              </div>

              {assignType === "package" ? (
                <Select value={formData.package_id} onValueChange={(v) => setFormData({ ...formData, package_id: v, bundle_id: "" })}>
                  <SelectTrigger className="bg-neutral-800 border-neutral-700 text-white" data-testid="assign-package-select">
                    <SelectValue placeholder="Paket seçin" />
                  </SelectTrigger>
                  <SelectContent className="bg-neutral-800 border-neutral-700">
                    {assignPackages.length === 0 ? (
                      <div className="px-2 py-1.5 text-sm text-neutral-500">Bu dilde paket yok</div>
                    ) : assignPackages.map((pkg) => (
                      <SelectItem key={pkg.id} value={pkg.id} className="text-white">{pkg.name} - €{pkg.price}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="space-y-2">
                  <Select value={formData.bundle_id} onValueChange={(v) => setFormData({ ...formData, bundle_id: v, package_id: "" })}>
                    <SelectTrigger className="bg-neutral-800 border-neutral-700 text-white" data-testid="assign-bundle-select">
                      <SelectValue placeholder="Kombo seçin" />
                    </SelectTrigger>
                    <SelectContent className="bg-neutral-800 border-neutral-700">
                      {assignBundles.map((bundle) => (
                        <SelectItem key={bundle.id} value={bundle.id} className="text-white">
                          {bundle.name} - €{bundle.price} ({bundle.package_ids?.length || 0} paket, %{bundle.discount_percentage} indirim)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {assignBundles.length === 0 && (
                    <p className="text-xs text-amber-500">Bu dilde kombo tanımlanmamış</p>
                  )}
                </div>
              )}

              <Button 
                type="submit" 
                className={assignType === "bundle" ? "w-full bg-amber-600 hover:bg-amber-700" : "w-full bg-red-600 hover:bg-red-700"}
                disabled={saving || (assignType === "package" ? !formData.package_id : !formData.bundle_id)} 
                data-testid="confirm-assign-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {assignType === "bundle" ? "Kombo Ata" : "Paket Ata"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={(open) => {
          setIsEditDialogOpen(open);
          if (!open) {
            setSelectedUser(null);
            setEditFormData({ full_name: "", email: "", password: "" });
            setShowEditPassword(false);
          }
        }}>
          <DialogContent className="max-w-md bg-neutral-900 border-neutral-800" data-testid="edit-user-dialog">
            <DialogHeader>
              <DialogTitle className="text-white font-['Nunito']">Kullanıcı Bilgilerini Düzenle</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-neutral-300">Ad Soyad</Label>
                <Input
                  value={editFormData.full_name}
                  onChange={(e) => setEditFormData({ ...editFormData, full_name: e.target.value })}
                  className="bg-neutral-800 border-neutral-700 text-white"
                  data-testid="edit-user-fullname-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-neutral-300">E-mail</Label>
                <Input
                  type="email"
                  value={editFormData.email}
                  onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                  className="bg-neutral-800 border-neutral-700 text-white"
                  data-testid="edit-user-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-neutral-300">Yeni Şifre <span className="text-neutral-500 text-xs">(en az 6 karakter — boş bırakırsanız değiştirilmez)</span></Label>
                <div className="relative">
                  <Input
                    type={showEditPassword ? "text" : "password"}
                    value={editFormData.password}
                    onChange={(e) => setEditFormData({ ...editFormData, password: e.target.value })}
                    placeholder="••••••••"
                    autoComplete="new-password"
                    className="bg-neutral-800 border-neutral-700 text-white pr-10"
                    data-testid="edit-user-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowEditPassword(!showEditPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-white"
                    data-testid="toggle-edit-password-visibility"
                  >
                    {showEditPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" className="w-full bg-red-600 hover:bg-red-700" disabled={saving} data-testid="save-edit-user-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Kaydet
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Stats / Progress Dialog */}
        <Dialog open={isStatsDialogOpen} onOpenChange={(open) => {
          setIsStatsDialogOpen(open);
          if (!open) {
            setStatsData(null);
            setSelectedUser(null);
          }
        }}>
          <DialogContent className="max-w-3xl bg-neutral-900 border-neutral-800 max-h-[85vh] overflow-y-auto" data-testid="stats-dialog">
            <DialogHeader>
              <DialogTitle className="text-white font-['Nunito']">
                Gelişim Raporu — {selectedUser?.full_name}
              </DialogTitle>
            </DialogHeader>
            {statsLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="w-8 h-8 animate-spin text-red-500" />
              </div>
            ) : statsData ? (
              <div className="space-y-4">
                {/* Overall Card */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-4 rounded-lg bg-neutral-800 border border-neutral-700">
                    <p className="text-xs text-neutral-400">Toplam Oturum</p>
                    <p className="text-2xl font-bold text-white" data-testid="stats-total-sessions">
                      {statsData.overall.total_sessions}
                    </p>
                  </div>
                  <div className="p-4 rounded-lg bg-neutral-800 border border-neutral-700">
                    <p className="text-xs text-neutral-400">Doğru Cevap</p>
                    {(() => {
                      const acc = statsData.overall.average_accuracy;
                      const colorCls = acc >= 80 ? "text-emerald-400" : acc >= 50 ? "text-amber-400" : "text-red-400";
                      return (
                        <p className={`text-2xl font-bold ${colorCls}`} data-testid="stats-overall-accuracy">
                          {acc}%
                        </p>
                      );
                    })()}
                  </div>
                </div>

                {/* Per-lesson breakdown */}
                {statsData.lessons.length === 0 ? (
                  <p className="text-neutral-400 text-sm py-6 text-center">Henüz tamamlanmış oturum yok.</p>
                ) : (
                  <div className="space-y-2">
                    <p className="text-sm text-neutral-300 font-semibold mt-2">Ders Bazlı Performans</p>
                    {statsData.lessons.map((l) => {
                      const acc = l.average_accuracy;
                      const colorBg = acc >= 80
                        ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                        : acc >= 50
                        ? "bg-amber-500/20 text-amber-400 border-amber-500/30"
                        : "bg-red-500/20 text-red-400 border-red-500/30";
                      return (
                        <div key={l.lesson_id} className="p-3 rounded-lg bg-neutral-800 border border-neutral-700">
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-white truncate">{l.lesson_title}</p>
                              <p className="text-xs text-neutral-500 mt-0.5">
                                {l.session_count} oturum • en iyi {l.best_accuracy}%
                              </p>
                            </div>
                            <span className={`px-2 py-1 rounded-md text-sm font-bold border ${colorBg}`}>
                              {acc}%
                            </span>
                          </div>
                          {/* Session timeline */}
                          {l.sessions.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-3">
                              {l.sessions.map((s, idx) => {
                                const sAcc = s.accuracy;
                                const sColor = sAcc >= 80
                                  ? "bg-emerald-600/30 text-emerald-300"
                                  : sAcc >= 50
                                  ? "bg-amber-600/30 text-amber-300"
                                  : "bg-red-600/30 text-red-300";
                                const dateStr = s.started_at ? new Date(s.started_at).toLocaleDateString("tr-TR") : "—";
                                return (
                                  <span
                                    key={idx}
                                    className={`text-[11px] px-2 py-0.5 rounded ${sColor}`}
                                    title={`${dateStr} • ${s.correct}/${s.total}`}
                                  >
                                    {idx + 1}. {sAcc}%
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-neutral-400 text-sm">Veri yüklenemedi.</p>
            )}
          </DialogContent>
        </Dialog>

        {/* Users Table */}
        <Card className="border-neutral-800 bg-neutral-900">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
              </div>
            ) : (
              <>
                <div className="p-4 border-b border-neutral-800">
                  <div className="relative max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                    <Input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Ad, soyad veya e-posta ile ara..."
                      className="pl-9 bg-neutral-800 border-neutral-700 text-white placeholder:text-neutral-500"
                      data-testid="user-search-input"
                    />
                  </div>
                </div>
                {/* Desktop table (unchanged on lg+) */}
                <div className="hidden lg:block overflow-x-auto">
                <Table>
                <TableHeader>
                  <TableRow className="border-neutral-800">
                    <TableHead className="text-neutral-400">Ad Soyad</TableHead>
                    <TableHead className="text-neutral-400">E-posta</TableHead>
                    <TableHead className="text-neutral-400">Paket</TableHead>
                    <TableHead className="text-neutral-400">Paket Detayları</TableHead>
                    <TableHead className="text-neutral-400">Durum</TableHead>
                    <TableHead className="text-neutral-400">İşlem</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {usersData.users
                    .filter(u => u.role !== "admin")
                    .filter(u => {
                      const q = searchTerm.trim().toLowerCase();
                      if (!q) return true;
                      return (
                        (u.full_name || "").toLowerCase().includes(q) ||
                        (u.email || "").toLowerCase().includes(q)
                      );
                    })
                    .map((user) => (
                    <TableRow key={user.id} className="border-neutral-800" data-testid={`user-row-${user.id}`}>
                      <TableCell className="font-medium text-white" style={{verticalAlign: 'top'}}>{user.full_name}</TableCell>
                      <TableCell className="text-neutral-400" style={{verticalAlign: 'top'}}>{user.email}</TableCell>
                      <TableCell className="text-neutral-300" style={{verticalAlign: 'top'}}>
                        {user.bundle_name 
                          ? <span className="flex items-center gap-1">
                              <span className="text-amber-400">📦</span>
                              {user.bundle_name}
                              <span className="text-xs text-neutral-500">({user.package_ids?.length || 0} paket)</span>
                            </span>
                          : user.package_name || "-"
                        }
                      </TableCell>
                      <TableCell className="text-neutral-300" style={{verticalAlign: 'top'}}>
                        {user.package_dates && Object.keys(user.package_dates).length > 0 ? (
                          <div className="space-y-2">
                            {Object.entries(user.package_dates).map(([pkgId, info]) => {
                              const remaining = Math.max(0, (info.total_sessions || 60) - (info.sessions_used || 0));
                              return (
                                <div key={pkgId} className="text-xs bg-neutral-800 rounded-lg p-2">
                                  <div className="font-medium text-white mb-1">{info.package_name || pkgId.substring(0, 8)}</div>
                                  <div className="flex gap-3 text-neutral-400">
                                    <span>Başlangıç: {info.start_date ? formatDate(info.start_date) : '—'}</span>
                                    <span>Bitiş: {info.end_date ? formatDate(info.end_date) : '—'}</span>
                                    <span className="text-neutral-200">Kalan: {remaining} / {info.total_sessions || 60}</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-xs">
                            <div>Başlangıç: {formatDate(user.subscription_start)}</div>
                            <div>Bitiş: {formatDate(user.subscription_end)}</div>
                            <div>Kalan: {user.total_sessions > 0 ? `${Math.max(0, user.total_sessions - (user.sessions_used || 0))} / ${user.total_sessions}` : '-'}</div>
                          </div>
                        )}
                      </TableCell>
                      <TableCell style={{verticalAlign: 'top'}}>
                        <Badge className={user.status === "AKTİF" ? "bg-emerald-600/20 text-emerald-500 border-emerald-600/30" : "bg-neutral-700 text-neutral-400"}>
                          {user.status}
                        </Badge>
                      </TableCell>
                      <TableCell style={{verticalAlign: 'top'}}>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="border-neutral-700 text-neutral-300 hover:bg-neutral-800 hover:text-white"
                            onClick={() => {
                              setSelectedUser(user);
                              setAssignLang(user.learning_language || localStorage.getItem("admin_lang") || "en");
                              setFormData(prev => ({ ...prev, package_id: "", bundle_id: "" }));
                              setIsPackageDialogOpen(true);
                            }}
                            data-testid={`assign-package-btn-${user.id}`}
                          >
                            Paket Ata
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="hover:bg-neutral-800 text-neutral-400 hover:text-red-500"
                            onClick={() => handleOpenStats(user)}
                            data-testid={`stats-btn-${user.id}`}
                            title="Gelişim Raporu"
                          >
                            <BarChart3 className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="hover:bg-neutral-800 text-neutral-400 hover:text-white"
                            onClick={() => handleOpenEditDialog(user)}
                            data-testid={`edit-user-btn-${user.id}`}
                            title="Kullanıcıyı Düzenle"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className={user.is_active === false ? "hover:bg-emerald-900/40 text-emerald-500 hover:text-emerald-400" : "hover:bg-amber-900/40 text-neutral-400 hover:text-amber-500"}
                            onClick={() => handleToggleActive(user)}
                            data-testid={`toggle-active-btn-${user.id}`}
                            title={user.is_active === false ? "Aktifleştir" : "Dondur (Pasife Al)"}
                          >
                            <Power className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="hover:bg-red-900/50 text-neutral-400 hover:text-red-500"
                            onClick={() => handleDeleteUser(user.id, user.full_name)}
                            data-testid={`delete-user-btn-${user.id}`}
                            title="Kullanıcıyı Sil"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
                </div>

                {/* Mobile card list (mobile only) */}
                <div className="lg:hidden divide-y divide-neutral-800" data-testid="admin-users-mobile-list">
                  {usersData.users
                    .filter(u => u.role !== "admin")
                    .filter(u => {
                      const q = searchTerm.trim().toLowerCase();
                      if (!q) return true;
                      return (
                        (u.full_name || "").toLowerCase().includes(q) ||
                        (u.email || "").toLowerCase().includes(q)
                      );
                    })
                    .map((user) => (
                      <div key={user.id} className="p-4" data-testid={`user-card-${user.id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-semibold text-white truncate">{user.full_name}</p>
                            <p className="text-sm text-neutral-400 truncate">{user.email}</p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Badge className={user.status === "AKTİF" ? "bg-emerald-600/20 text-emerald-500 border-emerald-600/30" : "bg-neutral-700 text-neutral-400"}>
                              {user.status}
                            </Badge>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="hover:bg-neutral-800 text-neutral-400 hover:text-white" data-testid={`user-actions-btn-${user.id}`}>
                                  <MoreVertical className="w-5 h-5" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="bg-neutral-900 border-neutral-700">
                                <DropdownMenuItem
                                  className="cursor-pointer text-neutral-200"
                                  onClick={() => {
                                    setSelectedUser(user);
                                    setAssignLang(user.learning_language || localStorage.getItem("admin_lang") || "en");
                                    setFormData(prev => ({ ...prev, package_id: "", bundle_id: "" }));
                                    setIsPackageDialogOpen(true);
                                  }}
                                  data-testid={`assign-package-btn-mobile-${user.id}`}
                                >
                                  <Plus className="w-4 h-4 mr-2" /> Paket Ata
                                </DropdownMenuItem>
                                <DropdownMenuItem className="cursor-pointer text-neutral-200" onClick={() => handleOpenStats(user)} data-testid={`stats-btn-mobile-${user.id}`}>
                                  <BarChart3 className="w-4 h-4 mr-2" /> Gelişim Raporu
                                </DropdownMenuItem>
                                <DropdownMenuItem className="cursor-pointer text-neutral-200" onClick={() => handleOpenEditDialog(user)} data-testid={`edit-user-btn-mobile-${user.id}`}>
                                  <Pencil className="w-4 h-4 mr-2" /> Düzenle
                                </DropdownMenuItem>
                                <DropdownMenuItem className="cursor-pointer text-neutral-200" onClick={() => handleToggleActive(user)} data-testid={`toggle-active-btn-mobile-${user.id}`}>
                                  <Power className="w-4 h-4 mr-2" /> {user.is_active === false ? "Aktifleştir" : "Dondur (Pasife Al)"}
                                </DropdownMenuItem>
                                <DropdownMenuItem className="cursor-pointer text-red-500 focus:text-red-500" onClick={() => handleDeleteUser(user.id, user.full_name)} data-testid={`delete-user-btn-mobile-${user.id}`}>
                                  <Trash2 className="w-4 h-4 mr-2" /> Sil
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>

                        <div className="mt-3 text-sm text-neutral-300">
                          <span className="text-neutral-500">Paket: </span>
                          {user.bundle_name
                            ? <span>📦 {user.bundle_name} <span className="text-xs text-neutral-500">({user.package_ids?.length || 0} paket)</span></span>
                            : (user.package_name || "-")}
                        </div>

                        <div className="mt-2">
                          {user.package_dates && Object.keys(user.package_dates).length > 0 ? (
                            <div className="space-y-2">
                              {Object.entries(user.package_dates).map(([pkgId, info]) => {
                                const remaining = Math.max(0, (info.total_sessions || 60) - (info.sessions_used || 0));
                                return (
                                  <div key={pkgId} className="text-xs bg-neutral-800 rounded-lg p-2">
                                    <div className="font-medium text-white mb-1">{info.package_name || pkgId.substring(0, 8)}</div>
                                    <div className="flex flex-wrap gap-x-3 gap-y-1 text-neutral-400">
                                      <span>Başlangıç: {info.start_date ? formatDate(info.start_date) : '—'}</span>
                                      <span>Bitiş: {info.end_date ? formatDate(info.end_date) : '—'}</span>
                                      <span className="text-neutral-200">Kalan: {remaining} / {info.total_sessions || 60}</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="text-xs text-neutral-400">
                              <div>Başlangıç: {formatDate(user.subscription_start)}</div>
                              <div>Bitiş: {formatDate(user.subscription_end)}</div>
                              <div>Kalan: {user.total_sessions > 0 ? `${Math.max(0, user.total_sessions - (user.sessions_used || 0))} / ${user.total_sessions}` : '-'}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
